import * as React from "react";
import { Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import {
  AlertState,
  SiteCameras,
  SiteSchedule,
  SiteInfo,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { handleSyncSite } from "./SiteAboutPageUtils/handleSyncSite";
import { getNumbersAfterAboutSite } from "./SiteAbout";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { overrideBannerText } from "./SiteAboutPageUtils/scheduleOverrides";
import {
  IDLE_CONFIGURE_MOTION,
  configureMotionSeverity,
} from "./SiteAboutPageUtils/configureMotionStatus";
import { useSyncBannersHiddenForCurrentSite } from "../../../utils/syncBannerPreference";
import { HideSyncBannerCheckbox } from "../../../common/HideSyncBannerCheckbox";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const SiteAboutAlerts = ({
  siteDeployed,
  aiLinkSite,
  setBackdropLoadingScreenOn,
  setSyncNeeded,
  setAlertState,
  siteInfo,
  setSiteInfo,
  setAllSiteSchedules,
  setActiveTab,
  vmsVariables,
  setSiteDeployed,
  setDeativateInquiryDialogOpen,
  setSiteCameras,
  camerasNotDeployed,
  setCamerasNotDeployed,
  navigate,
  setVmsVariables,
  alertState,
  setEditMode,
  setClickedTab,
  setArmButtonDisabled,
  setArmedStatus,
  setRebootDialogOpen,
}: {
  siteDeployed: boolean;
  aiLinkSite: boolean;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setSyncNeeded: Dispatcher<boolean>;
  setAlertState: Dispatcher<AlertState>;
  siteInfo: SiteInfo;
  setSiteInfo: Dispatcher<SiteInfo>;
  setAllSiteSchedules: Dispatcher<SiteSchedule[]>;
  setActiveTab: Dispatcher<JSX.Element>;
  vmsVariables: (string | string[] | undefined)[][];
  setSiteDeployed: Dispatcher<boolean>;
  setDeativateInquiryDialogOpen: Dispatcher<boolean>;
  setSiteCameras: Dispatcher<SiteCameras>;
  camerasNotDeployed: boolean;
  setCamerasNotDeployed: Dispatcher<boolean>;
  navigate: NavigateFunction;
  setVmsVariables: Dispatcher<(string | string[] | undefined)[][]>;
  alertState: AlertState;
  setEditMode: Dispatcher<boolean>;
  setClickedTab: Dispatcher<string>;
  setArmButtonDisabled: any;
  setArmedStatus: any;
  setRebootDialogOpen: Dispatcher<boolean>;
}) => {
  const {
    siteAboutAlerts,
    setSiteAboutAlerts,
    setScheduleGridEditable,
    syncSiteNeededAlertOpen,
    setSyncSiteNeededAlertOpen,
    setSyncSiteNeeded,
  } = useSiteAboutStore();
  const isSyncExcludedIntegration =
    siteInfo.integration_type_name === "autopatrol" ||
    siteInfo.integration_type_name === "clips";
  // "Don't show again for this site" on any sync banner hides the reminder for
  // this site for the rest of the browser session (SUP-924). The header Sync
  // Site button still turns red while changes are pending, so nothing is lost
  // — only the nag.
  const syncBannersHidden = useSyncBannersHiddenForCurrentSite();
  const closeSyncNeededAlert = () => {
    setAlertState((previousState: AlertState) => {
      return { ...previousState, changeSuccess: false };
    });
    setSyncSiteNeededAlertOpen(false);
    setEditMode(false);
    setScheduleGridEditable(false);
  };
  // With the reminder hidden, a save still needs a confirmation: show a plain
  // "Changes saved" that clears itself, instead of nothing at all.
  const showSavedConfirmationOnly =
    alertState.changeSuccess && !isSyncExcludedIntegration && syncBannersHidden;
  React.useEffect(() => {
    if (!showSavedConfirmationOnly) return;
    const timer = setTimeout(() => {
      setAlertState((previousState: AlertState) => {
        return { ...previousState, changeSuccess: false };
      });
    }, 6000);
    return () => clearTimeout(timer);
  }, [showSavedConfirmationOnly]);

  // ENG-2631: a holiday/one-off override replaces the regular schedule today
  // (SUP-954). Nothing when admin doesn't send the fields (old prod admin).
  const overrideBanner = overrideBannerText(siteInfo);

  return (
    <div>
      {!siteDeployed && !aiLinkSite && !isSyncExcludedIntegration && (
        <Alert
          severity="warning"
          onClose={() => {
            setSiteDeployed(true);
          }}
        >
          <p className="font-mulish font-bold text-[16px]">
            Site not deployed.{" "}
          </p>
          <p className="font-mulish text-[15px]">
            Please&nbsp;
            <Link
              to={`/sites/aboutsite/${getNumbersAfterAboutSite(location.href)}`}
              onClick={() => {
                setSiteDeployed(true);
                setBackdropLoadingScreenOn(true);
                handleSyncSite(
                  setBackdropLoadingScreenOn,
                  setAlertState,
                  siteInfo,
                  setSiteInfo,
                  setSyncNeeded,
                  setAllSiteSchedules,
                  setActiveTab,
                  vmsVariables,
                  setSiteDeployed,
                  setDeativateInquiryDialogOpen,
                  setSiteCameras,
                  camerasNotDeployed,
                  setCamerasNotDeployed,
                  navigate,
                  setVmsVariables,
                  setClickedTab,
                  setArmButtonDisabled,
                  setArmedStatus,
                  setRebootDialogOpen,
                );
              }}
              className="underline underline-offset-1"
            >
              Sync Site
            </Link>
            &nbsp;to deploy site.
          </p>
        </Alert>
      )}
      {alertState.noCameraOrSchedule &&
        !aiLinkSite &&
        localStorage.noAutoAddCamerasDialogOpen === "false" && (
          <Alert severity="warning">
            <p className="font-mulish font-bold text-[16px]">
              Incomplete Information
            </p>
            <p className="font-mulish text-[15px]">
              Please&nbsp;
              {siteInfo.auto_add ? (
                <Link
                  to="/sites/autoaddcamera"
                  className="underline underline-offset-1"
                >
                  Add Cameras
                </Link>
              ) : (
                <Link
                  to="/sites/addCamera"
                  className="underline underline-offset-1"
                  onClick={() => {
                    localStorage.setItem("firstCameraAdded", "true");
                    localStorage.setItem("copyValues", "true");
                  }}
                >
                  Add Cameras
                </Link>
              )}
              &nbsp;and then add schedule to run the Site.
            </p>
          </Alert>
        )}
      {alertState.noSchedule &&
        !aiLinkSite &&
        Boolean(siteInfo.display_schedule) &&
        Boolean(siteInfo.active) === true && (
          <Alert
            severity="warning"
            onClose={() => {
              setAlertState((previousState: AlertState) => {
                return { ...previousState, noSchedule: false };
              });
            }}
          >
            <p className="font-mulish font-bold text-[16px]">
              Incomplete Information
            </p>
            <p className="font-mulish text-[15px]">
              Please add schedule to run the Site.
            </p>
          </Alert>
        )}
      {overrideBanner && (
        <Alert severity="info" data-testid="site-override-banner">
          <b>{overrideBanner}.</b> It replaces the regular schedule today, so
          the site arms and disarms only as the override says. See the
          Schedules tab for its hours.
        </Alert>
      )}
      {alertState.activateSuccess && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, activateSuccess: false };
            });
          }}
        >
          Site successfully activated.
        </Alert>
      )}
      {alertState.deactivateSuccess && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, deactivateSuccess: false };
            });
          }}
        >
          Site successfully deactivated.
        </Alert>
      )}
      {(alertState.changeSuccess || syncSiteNeededAlertOpen) &&
        !isSyncExcludedIntegration &&
        !syncBannersHidden && (
          <Alert
            severity="warning"
            action={
              <>
                <HideSyncBannerCheckbox onHide={closeSyncNeededAlert} />
                <IconButton
                  aria-label="Close"
                  color="inherit"
                  size="small"
                  onClick={closeSyncNeededAlert}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </>
            }
          >
            Changes saved successfully! Please&nbsp;
            <Link
              to={`/sites/aboutsite/${getNumbersAfterAboutSite(location.href)}`}
              onClick={() => {
                setBackdropLoadingScreenOn(true);
                setSyncSiteNeededAlertOpen(false);
                handleSyncSite(
                  setBackdropLoadingScreenOn,
                  setAlertState,
                  siteInfo,
                  setSiteInfo,
                  setSyncNeeded,
                  setAllSiteSchedules,
                  setActiveTab,
                  vmsVariables,
                  setSiteDeployed,
                  setDeativateInquiryDialogOpen,
                  setSiteCameras,
                  camerasNotDeployed,
                  setCamerasNotDeployed,
                  navigate,
                  setVmsVariables,
                  setClickedTab,
                  setArmButtonDisabled,
                  setArmedStatus,
                  setRebootDialogOpen,
                );
              }}
              className="underline underline-offset-1"
            >
              Sync Site
            </Link>
            &nbsp;to apply changes.
          </Alert>
        )}
      {showSavedConfirmationOnly && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, changeSuccess: false };
            });
          }}
        >
          Changes saved successfully.
        </Alert>
      )}
      {alertState.disarmSuccess && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, disarmSuccess: false };
            });
          }}
        >
          Site disarm command sent successfully. It may take a few moments for
          site arm status to update.
        </Alert>
      )}
      {alertState.armSuccess && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, armSuccess: false };
            });
          }}
        >
          Site arm command sent successfully. It may take a few moments for arm
          status to update.
        </Alert>
      )}
      {alertState.armFail && (
        <Alert
          severity="warning"
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, armFail: false };
            });
          }}
        >
          Site arm command failed. Please try again
        </Alert>
      )}
      {alertState.syncSuccess && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, syncSuccess: false };
            });
          }}
        >
          Site synced successfully.
        </Alert>
      )}
      {alertState.configureMotion.phase !== "idle" && (
        <Alert
          severity={configureMotionSeverity(alertState.configureMotion.phase)}
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return {
                ...previousState,
                configureMotion: IDLE_CONFIGURE_MOTION,
              };
            });
          }}
        >
          {alertState.configureMotion.message}
        </Alert>
      )}
      {alertState.refreshCameraSuccess && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, refreshCameraSuccess: false };
            });
          }}
        >
          Cameras refreshed successfully.
        </Alert>
      )}
      {alertState.refreshCameraFail && (
        <Alert
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, refreshCameraFail: false };
            });
          }}
        >
          Cameras failed to refresh. Please try again.
        </Alert>
      )}
      {alertState.webhookChangesFail && (
        <Alert
          severity="error"
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, webhookChangesFail: false };
            });
          }}
        >
          Webhooks updates failed to save. Please try again.
        </Alert>
      )}
      {alertState.cameraDeleteSuccess && !isSyncExcludedIntegration && (
        <Alert
          severity="warning"
          onClose={() => {
            setAlertState((previousState: AlertState) => {
              return { ...previousState, cameraDeleteSuccess: false };
            });
          }}
        >
          Camera delete successful! Please{" "}
          <Link
            to={`/sites/aboutsite/${getNumbersAfterAboutSite(location.href)}`}
            onClick={() => {
              setBackdropLoadingScreenOn(true);
              setSyncNeeded(false);
              handleSyncSite(
                setBackdropLoadingScreenOn,
                setAlertState,
                siteInfo,
                setSiteInfo,
                setSyncNeeded,
                setAllSiteSchedules,
                setActiveTab,
                vmsVariables,
                setSiteDeployed,
                setDeativateInquiryDialogOpen,
                setSiteCameras,
                camerasNotDeployed,
                setCamerasNotDeployed,
                navigate,
                setVmsVariables,
                setClickedTab,
                setArmButtonDisabled,
                setArmedStatus,
                setRebootDialogOpen,
              );
            }}
            className="underline underline-offset-1"
          >
            Sync Site
          </Link>
          &nbsp;to apply changes.
        </Alert>
      )}
      {camerasNotDeployed && !isSyncExcludedIntegration && (
        <Alert
          severity="warning"
          onClose={() => {
            setCamerasNotDeployed(false);
          }}
        >
          One or more cameras has changes which have not been deployed! Please{" "}
          <Link
            to={`/sites/aboutsite/${getNumbersAfterAboutSite(location.href)}`}
            onClick={() => {
              setBackdropLoadingScreenOn(true);
              handleSyncSite(
                setBackdropLoadingScreenOn,
                setAlertState,
                siteInfo,
                setSiteInfo,
                setSyncNeeded,
                setAllSiteSchedules,
                setActiveTab,
                vmsVariables,
                setSiteDeployed,
                setDeativateInquiryDialogOpen,
                setSiteCameras,
                camerasNotDeployed,
                setCamerasNotDeployed,
                navigate,
                setVmsVariables,
                setClickedTab,
                setArmButtonDisabled,
                setArmedStatus,
                setRebootDialogOpen,
              );
            }}
            className="underline underline-offset-1"
          >
            Sync Site
          </Link>
          &nbsp;to apply changes.
        </Alert>
      )}
      {siteAboutAlerts.scheduleSubmittedAndSiteSynced && (
        <Alert
          onClose={() => {
            setSiteAboutAlerts({
              ...siteAboutAlerts,
              scheduleSubmittedAndSiteSynced: false,
            });
          }}
        >
          Schedule changes submitted and site synced successfully.
        </Alert>
      )}
    </div>
  );
};
